import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useClickOutside } from '../hooks/useClickOutside';
import { useInputHandling } from '../hooks/useInputHandling';
import { useInvoiceStore } from '../stores/invoiceStore';

interface InlineEditProps {
  isEditing: boolean;
  onEditClick: () => void;
  onSave: () => void;
  displayContent: React.ReactNode;
  editContent: React.ReactNode;
  isPlaceholder?: {
    [key: string]: boolean;
  };
  showDefaultSaveButton?: boolean;
}

const InlineEdit: React.FC<InlineEditProps> = ({
  isEditing,
  onEditClick,
  onSave,
  displayContent,
  editContent,
  isPlaceholder = {},
  showDefaultSaveButton = true,
}) => {
  const { t } = useTranslation();
  const editRef = useRef<HTMLDivElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  
  // Check if invoice is editable based on its status and payment status
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const isInvoiceEditable = currentInvoice.status !== 'sent' && currentInvoice.paymentStatus !== 'paid';
  
  useClickOutside(editRef, () => {
    if (isEditing) {
      onSave();
    }
  });

  const { handleKeyDown } = useInputHandling({
    onSave,
    isEditing
  });
  
  return (
    <div className="relative">
      {/* Always render display content to maintain layout height */}
      <div 
        ref={displayRef}
        className={`rounded-xl p-4 ${isEditing ? 'invisible' : 'visible'} ${
          isInvoiceEditable 
            ? 'cursor-pointer hover:bg-gray-50' 
            : 'cursor-not-allowed pointer-events-none'
        } transition-all duration-200 group`}
        onClick={!isEditing && isInvoiceEditable ? onEditClick : undefined}
      >
        {displayContent}
        {isInvoiceEditable && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-lg">
              {Object.values(isPlaceholder).some(Boolean) ? t('placeholders.addDetails') : t('actions.clickToEdit')}
            </span>
          </div>
        )}
      </div>

      {/* Edit form overlay */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            ref={editRef}
            key="edit"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-0 left-0 z-[60] min-w-full -m-4"
            onKeyDown={handleKeyDown}
          >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-[0_0_20px_rgba(0,0,0,0.08)] p-6 space-y-4">
              {editContent}
              {showDefaultSaveButton && (
                <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={onSave}
                    className="btn-primary btn-sm"
                  >
                    {t('actions.save')}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InlineEdit; 